import { Car, Edit2, Save, X, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

import React, { useState, useMemo, useEffect } from "react";
import { useDealerPortal } from "@/hooks/useDealerPortal";
import type { DealerPortalVehicle } from "@/types/dealerPortal";
import { MakeSelectionDialog } from "@/admin/car-card/dialogs/MakeSelectionDialog";
import { ModelSelectionDialog } from "@/admin/car-card/dialogs/ModelSelectionDialog";
import { EngineSelectionDialog } from "@/admin/car-card/dialogs/EngineSelectionDialog";
import { EquipmentSelectionDialog } from "@/components/valuation/form-sections/EquipmentSelectionDialog";
import { formatMileage } from "@/utils/listingUtils";
import { useLanguage } from "@/context/LanguageContext";
import {
  conditionGradeLabels,
  fuelTypeLabels,
  transmissionTypeLabels,
  bodyTypeLabels,
  colorLabels,
  labelToCondition,
  labelToFuelType,
  labelToTransmission,
  labelToBodyType,
  labelToColor,
} from "@/i18n/labels/vehicleLabels";
import {
  vehicleCardUILabels,
  vehicleCardToastLabels,
  commonMiscLabels,
  getEquipmentLabel,
} from "@/i18n";
import {
  ConditionGrade,
  VehicleFuelType,
  TransmissionType,
} from "@/types/enums";

interface DealerPortalVehicleCarDetailsProps {
  vehicle: DealerPortalVehicle;
  onUpdated?: () => void;
}

const initialEquipmentCount = 6;

export const DealerPortalVehicleCarDetails = ({
  vehicle,
  onUpdated,
}: DealerPortalVehicleCarDetailsProps) => {
  const { language } = useLanguage();

  const { updateVehicle } = useDealerPortal();
  const [showAllEquipment, setShowAllEquipment] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [makeDialogOpen, setMakeDialogOpen] = useState(false);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [engineDialogOpen, setEngineDialogOpen] = useState(false);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);

  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [variant, setVariant] = useState("");
  const [year, setYear] = useState(0);
  const [condition, setCondition] = useState("");
  const [mileage, setMileage] = useState(0);
  const [engine, setEngine] = useState("");
  const [transmission, setTransmission] = useState("");
  const [bodyType, setBodyType] = useState("");
  const [color, setColor] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [horsepower, setHorsepower] = useState("");
  const [engineSize, setEngineSize] = useState("");
  const [vin, setVin] = useState("");
  const [equipment, setEquipment] = useState<string[]>([]);

  const isUpdating = updateVehicle.isPending;

  const hasChanges = useMemo(() => {
    const v = vehicle;
    const condLabels = conditionGradeLabels(language);
    const transLabels = transmissionTypeLabels(language);
    const bodyLabels = bodyTypeLabels(language);
    const colorLabelsMap = colorLabels(language);
    const fuelLabels = fuelTypeLabels(language);
    const currentConditionTech = condition
      ? (Object.entries(condLabels).find(([, l]) => l === condition)?.[0] ??
        condition)
      : "";
    const currentTransTech = transmission
      ? (Object.entries(transLabels).find(([, l]) => l === transmission)?.[0] ??
        transmission)
      : "";
    const currentBodyTech = bodyType
      ? (Object.entries(bodyLabels).find(([, l]) => l === bodyType)?.[0] ??
        bodyType)
      : "";
    const currentColorTech = color
      ? (Object.entries(colorLabelsMap).find(([, l]) => l === color)?.[0] ??
        color)
      : "";
    const currentFuelTech = fuelType
      ? (Object.entries(fuelLabels).find(([, l]) => l === fuelType)?.[0] ??
        fuelType)
      : "";
    return (
      make !== (v.make ?? "") ||
      model !== (v.model ?? "") ||
      variant !== (v.variant ?? "") ||
      year !== (v.year ?? 0) ||
      currentConditionTech !== (v.condition ?? "") ||
      mileage !== (v.mileage ?? 0) ||
      engine !== (v.engine ?? "") ||
      currentTransTech !== (v.transmission ?? "") ||
      currentBodyTech !== (v.bodyType ?? "") ||
      currentColorTech !== (v.color ?? "") ||
      currentFuelTech !== (v.fuelType ?? "") ||
      horsepower !== (v.horsepower != null ? String(v.horsepower) : "") ||
      engineSize !== (v.engineSize ?? "") ||
      vin !== (v.vin ?? "") ||
      equipment.length !== (v.equipment?.length ?? 0) ||
      (v.equipment ?? []).some((item, i) => item !== equipment[i])
    );
  }, [
    vehicle,
    make,
    model,
    variant,
    year,
    condition,
    mileage,
    engine,
    transmission,
    bodyType,
    color,
    fuelType,
    horsepower,
    engineSize,
    vin,
    equipment,
    language,
  ]);

  useEffect(() => {
    if (!vehicle) return;
    const v = vehicle;
    const lang = language;
    const condLabels = conditionGradeLabels(lang);
    const transLabels = transmissionTypeLabels(lang);
    const bodyLabels = bodyTypeLabels(lang);
    const colorLabelsMap = colorLabels(lang);
    const fuelLabels = fuelTypeLabels(lang);

    setMake(v.make ?? "");
    setModel(v.model ?? "");
    setVariant(v.variant ?? "");
    setYear(v.year ?? 0);
    setCondition(
      v.condition
        ? (condLabels[v.condition.toUpperCase() as ConditionGrade] ?? "")
        : "",
    );
    setMileage(v.mileage ?? 0);
    setEngine(v.engine ?? "");
    setTransmission(
      v.transmission
        ? (transLabels[v.transmission.toUpperCase() as TransmissionType] ?? "")
        : "",
    );
    setBodyType(
      v.bodyType ? (bodyLabels[v.bodyType.toLowerCase()] ?? v.bodyType) : "",
    );
    setColor(v.color ? (colorLabelsMap[v.color.toLowerCase()] ?? v.color) : "");
    setFuelType(
      v.fuelType
        ? (fuelLabels[v.fuelType.toUpperCase() as VehicleFuelType] ?? "")
        : "",
    );
    setHorsepower(v.horsepower != null ? String(v.horsepower) : "");
    setEngineSize(v.engineSize ?? "");
    setVin(v.vin ?? "");
    setEquipment(v.equipment ?? []);
  }, [vehicle, language]);

  const handleSave = async () => {
    const lang = language;
    const conditionReverseMap = labelToCondition(lang);
    const transmissionReverseMap = labelToTransmission(lang);
    const bodyTypeReverseMap = labelToBodyType(lang);
    const colorReverseMap = labelToColor(lang);
    const fuelTypeReverseMap = labelToFuelType(lang);

    const conditionTech = condition
      ? conditionReverseMap[condition] || condition
      : undefined;
    const transmissionTech = transmission
      ? transmissionReverseMap[transmission] || transmission
      : undefined;
    const bodyTypeTech = bodyType
      ? bodyTypeReverseMap[bodyType] || bodyType
      : undefined;
    const colorTech = color ? colorReverseMap[color] || color : undefined;
    const fuelTypeTech = fuelType
      ? fuelTypeReverseMap[fuelType] || fuelType
      : undefined;

    try {
      await updateVehicle.mutateAsync({
        id: vehicle.id,
        body: {
          make,
          model,
          year,
          variant: variant || null,
          condition: conditionTech ?? null,
          mileage,
          engine: engine || null,
          transmission: transmissionTech ?? null,
          bodyType: bodyTypeTech ?? null,
          color: colorTech ?? null,
          fuelType: fuelTypeTech ?? null,
          horsepower: horsepower ? parseInt(horsepower, 10) : null,
          engineSize: engineSize || null,
          vin: vin || null,
          equipment,
        },
      });

      const toastLabels = vehicleCardToastLabels(language);
      toast.success(toastLabels.vehicleDetailsUpdated, { description: toastLabels.vehicleDetailsUpdatedDescription });
      onUpdated?.();
      setIsEditing(false);
    } catch (error: unknown) {
      const uiLabels = vehicleCardUILabels(language);
      const toastLabels = vehicleCardToastLabels(language);
      let errorMessage = toastLabels.saveErrorDescription ?? "Save failed.";
      if (error && typeof error === "object" && "response" in error) {
        const err = error as { response?: { status?: number } };
        if (err.response?.status === 401) {
          errorMessage = uiLabels.unauthorized ?? "Unauthorized";
        }
      }
      if (error && typeof error === "object" && "message" in error) {
        const err = error as { message?: string };
        if (err.message) errorMessage = err.message;
      }
      if (error && typeof error === "object" && "details" in error) {
        const err = error as { details?: { message?: string } };
        if (err.details?.message) errorMessage = err.details.message;
      }
      toast.error(toastLabels.saveError ?? "Error", { description: errorMessage });
    }
  };

  const handleCancel = () => {
    const lang = language;
    const condLabels = conditionGradeLabels(lang);
    const transLabels = transmissionTypeLabels(lang);
    const bodyLabels = bodyTypeLabels(lang);
    const colorLabelsMap = colorLabels(lang);
    const fuelLabels = fuelTypeLabels(lang);
    const v = vehicle;

    setMake(v.make ?? "");
    setModel(v.model ?? "");
    setVariant(v.variant ?? "");
    setYear(v.year ?? 0);
    setCondition(
      v.condition
        ? (condLabels[v.condition.toUpperCase() as ConditionGrade] ?? "")
        : "",
    );
    setMileage(v.mileage ?? 0);
    setEngine(v.engine ?? "");
    setTransmission(
      v.transmission
        ? (transLabels[v.transmission.toUpperCase() as TransmissionType] ?? "")
        : "",
    );
    setBodyType(
      v.bodyType ? (bodyLabels[v.bodyType.toLowerCase()] ?? v.bodyType) : "",
    );
    setColor(v.color ? (colorLabelsMap[v.color.toLowerCase()] ?? v.color) : "");
    setFuelType(
      v.fuelType
        ? (fuelLabels[v.fuelType.toUpperCase() as VehicleFuelType] ?? "")
        : "",
    );
    setHorsepower(v.horsepower != null ? String(v.horsepower) : "");
    setEngineSize(v.engineSize ?? "");
    setVin(v.vin ?? "");
    setEquipment(v.equipment ?? []);
    setIsEditing(false);
  };

  const displayedEquipment = showAllEquipment
    ? equipment
    : equipment.slice(0, initialEquipmentCount);
  const uiLabels = vehicleCardUILabels(language);

  if (!vehicle) {
    return (
      <div className="text-center text-muted-foreground p-4">
        {uiLabels.vehicleDetailsNotLoaded}
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {isUpdating && (
        <div className="absolute inset-0 bg-secondary/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              {uiLabels.savingVehicleDetails}
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="p-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Car className="h-5 w-5" />
            {uiLabels.vehicleDetails}
            {isEditing && (
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isUpdating}
                >
                  <X className="h-8 w-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSave}
                  disabled={!hasChanges || isUpdating}
                >
                  {isUpdating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-8 w-8 text-blue-700 dark:text-blue-300" />
                  )}
                </Button>
              </div>
            )}
            {!isEditing && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => setIsEditing(true)}
                disabled={isUpdating}
              >
                <Edit2 className="h-8 w-8" />
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Reg
              </label>
              <div
                className={`text-sm font-medium ${
                  !vehicle.regNr ? "text-red-600 dark:text-red-400" : ""
                }`}
              >
                {vehicle.regNr || uiLabels.notSpecified}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {uiLabels.make}
              </label>
              {isEditing ? (
                <Button
                  variant="outline"
                  onClick={() => setMakeDialogOpen(true)}
                  className="h-8 w-full justify-start text-left font-normal"
                  disabled={isUpdating}
                >
                  {make || uiLabels.selectMake}
                </Button>
              ) : (
                <div className="text-sm font-medium">{make}</div>
              )}
            </div>

            <div className="space-y-2">
              <label
                className={`text-sm font-medium ${
                  isEditing && make && !model
                    ? "text-red-500"
                    : "text-muted-foreground"
                }`}
              >
                {uiLabels.model}
              </label>
              {isEditing ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!make) {
                      toast.error(uiLabels.selectMakeFirst, { description: uiLabels.selectMakeFirstDescription });
                      return;
                    }
                    setModelDialogOpen(true);
                  }}
                  className="h-8 w-full justify-start text-left font-normal"
                  disabled={isUpdating || !make}
                >
                  {model || uiLabels.selectModel}
                </Button>
              ) : (
                <div className="text-sm font-medium">{model}</div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Variant
              </label>
              {isEditing ? (
                <Input
                  value={variant}
                  onChange={(e) => setVariant(e.target.value)}
                  className="h-8"
                  disabled={isUpdating}
                />
              ) : (
                <div
                  className={`text-sm font-medium ${!variant ? "text-muted-foreground" : ""}`}
                >
                  {variant || commonMiscLabels(language).unknown}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {uiLabels.year}
              </label>
              {isEditing ? (
                <Input
                  type="number"
                  value={year}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setYear(isNaN(value) ? 0 : value);
                  }}
                  className="h-8"
                  disabled={isUpdating}
                />
              ) : (
                <div className="text-sm font-medium">{year}</div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {uiLabels.condition}
              </label>
              {isEditing ? (
                <Select
                  value={condition}
                  onValueChange={setCondition}
                  disabled={isUpdating}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder={uiLabels.selectCondition} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(conditionGradeLabels(language)).map(
                      ([techKey, label]) => (
                        <SelectItem key={techKey} value={label}>
                          {label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !condition ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {vehicle.condition
                    ? (conditionGradeLabels(language)[
                        vehicle.condition as ConditionGrade
                      ] ?? vehicle.condition)
                    : uiLabels.notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {uiLabels.mileage}
              </label>
              {isEditing ? (
                <Input
                  type="number"
                  value={mileage}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setMileage(isNaN(value) ? 0 : value);
                  }}
                  className="h-8"
                  disabled={isUpdating}
                />
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !mileage ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {mileage ? `${formatMileage(mileage)} mil` : "Ej angivet"}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label
                className={`text-sm font-medium ${
                  isEditing && make && !engine
                    ? "text-red-500"
                    : "text-muted-foreground"
                }`}
              >
                {uiLabels.engine}
              </label>
              {isEditing ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!make) {
                      toast.error(uiLabels.selectMakeFirst, { description: uiLabels.selectMakeFirstForEngine });
                      return;
                    }
                    setEngineDialogOpen(true);
                  }}
                  className="h-8 w-full justify-start text-left font-normal"
                  disabled={isUpdating || !make}
                >
                  {engine || uiLabels.selectEngine}
                </Button>
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !engine ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {engine || uiLabels.notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {uiLabels.transmission}
              </label>
              {isEditing ? (
                <Select
                  value={transmission}
                  onValueChange={setTransmission}
                  disabled={isUpdating}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder={uiLabels.selectTransmission} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(transmissionTypeLabels(language)).map(
                      ([techKey, label]) => (
                        <SelectItem key={techKey} value={label}>
                          {label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !transmission ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {vehicle.transmission
                    ? (transmissionTypeLabels(language)[
                        vehicle.transmission as TransmissionType
                      ] ?? vehicle.transmission)
                    : uiLabels.notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {uiLabels.bodyType}
              </label>
              {isEditing ? (
                <Select
                  value={bodyType}
                  onValueChange={setBodyType}
                  disabled={isUpdating}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="Välj karosstyp" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(bodyTypeLabels(language)).map(
                      ([techKey, label]) => (
                        <SelectItem key={techKey} value={label}>
                          {label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !bodyType ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {vehicle.bodyType
                    ? bodyTypeLabels(language)[
                        vehicle.bodyType.toLowerCase()
                      ] || vehicle.bodyType
                    : uiLabels.notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {uiLabels.color}
              </label>
              {isEditing ? (
                <Select
                  value={color}
                  onValueChange={setColor}
                  disabled={isUpdating}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder={uiLabels.selectColor} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(colorLabels(language)).map(
                      ([techKey, label]) => (
                        <SelectItem key={techKey} value={label}>
                          {label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !color ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {vehicle.color
                    ? colorLabels(language)[vehicle.color.toLowerCase()] ||
                      vehicle.color
                    : uiLabels.notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {uiLabels.fuelType}
              </label>
              {isEditing ? (
                <Select
                  value={fuelType}
                  onValueChange={setFuelType}
                  disabled={isUpdating}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder={uiLabels.selectFuelType} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(fuelTypeLabels(language)).map(
                      ([techKey, label]) => (
                        <SelectItem key={techKey} value={label}>
                          {label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !fuelType ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {vehicle.fuelType
                    ? (fuelTypeLabels(language)[
                        vehicle.fuelType as VehicleFuelType
                      ] ?? vehicle.fuelType)
                    : uiLabels.notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {uiLabels.horsepower}
              </label>
              {isEditing ? (
                <Input
                  type="number"
                  value={horsepower}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setHorsepower(isNaN(value) ? "" : String(value));
                  }}
                  className="h-8"
                  disabled={isUpdating}
                />
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !horsepower ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {horsepower || uiLabels.notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {uiLabels.engineSize}
              </label>
              {isEditing ? (
                <Input
                  value={engineSize}
                  onChange={(e) => setEngineSize(e.target.value)}
                  className="h-8"
                  placeholder={uiLabels.engineSizePlaceholder}
                  disabled={isUpdating}
                />
              ) : (
                <div
                  className={`text-sm font-medium ${!engineSize ? "text-muted-foreground" : ""}`}
                >
                  {engineSize || commonMiscLabels(language).unknown}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {uiLabels.vin}
              </label>
              {isEditing ? (
                <Input
                  value={vin}
                  onChange={(e) => setVin(e.target.value)}
                  className="h-8"
                  disabled={isUpdating}
                />
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !vin ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {vin || uiLabels.notSpecified}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-border pt-4 mt-4" />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-muted-foreground">
                {uiLabels.equipment}
              </label>
              {isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEquipmentDialogOpen(true)}
                  disabled={isUpdating}
                >
                  {uiLabels.selectEquipment ?? "Välj utrustning"}
                </Button>
              )}
            </div>

            {equipment.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {displayedEquipment.map((item, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {getEquipmentLabel(item, language)}
                  </Badge>
                ))}
                {equipment.length > initialEquipmentCount && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAllEquipment(!showAllEquipment)}
                    className="text-muted-foreground"
                    disabled={isUpdating}
                  >
                    {showAllEquipment
                      ? uiLabels.showLess
                      : (
                          uiLabels.moreEquipment ?? "Visa {count} till"
                        )?.replace(
                          "{count}",
                          String(equipment.length - initialEquipmentCount),
                        )}
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                {uiLabels.noEquipmentSpecified}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <MakeSelectionDialog
        open={makeDialogOpen}
        onOpenChange={setMakeDialogOpen}
        selectedMake={make}
        onSelect={(newMake) => {
          if (newMake !== make) {
            setMake(newMake);
            setModel("");
            setEngine("");
          }
        }}
        vehicleType="car"
      />

      <ModelSelectionDialog
        open={modelDialogOpen}
        onOpenChange={setModelDialogOpen}
        selectedMake={make}
        selectedModel={model}
        onSelect={(newModel) => {
          if (newModel !== model) {
            setModel(newModel);
            setEngine("");
          }
        }}
      />

      <EngineSelectionDialog
        open={engineDialogOpen}
        onOpenChange={setEngineDialogOpen}
        selectedMake={make}
        selectedEngine={engine}
        onSelect={setEngine}
      />

      <EquipmentSelectionDialog
        open={equipmentDialogOpen}
        onOpenChange={setEquipmentDialogOpen}
        selectedEquipment={equipment}
        onEquipmentChange={setEquipment}
        vehicleType="car"
      />
    </div>
  );
};
